import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginUser, verifyOtp } from '../services/api';
import { storeToken } from '../services/tokenStorage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faSpinner } from '@fortawesome/free-solid-svg-icons';
import './Login.css';

const Login = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [step, setStep] = useState('phone'); // 'phone' | 'otp'
  const [sessionInfo, setSessionInfo] = useState({}); // e.g., { session_id, transaction_id }
  const [serverMsg, setServerMsg] = useState('');
  const navigate = useNavigate();

  const handlePhoneNumberChange = (e) => {
    // Only allow digits and maximum 10 digits
    const phone = e.target.value.replace(/\D/g, '').slice(0, 10);
    setPhoneNumber(phone);
  };

  const validatePhoneNumber = (phone) => {
    // Validate phone number is exactly 10 digits
    return /^\d{10}$/.test(phone);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Validate phone number
    if (!validatePhoneNumber(phoneNumber)) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }
    
    setLoading(true);
    setError('');
    setServerMsg('');
    
    try {
      // Format with +91 prefix if not already present
      const formattedPhone = phoneNumber.startsWith('+91') ? phoneNumber : `+91${phoneNumber}`;
      
      // Call login API
      const response = await loginUser(formattedPhone);

      // If token is returned, we're done. Otherwise, assume OTP is required.
      if (response && response.token) {
        // Store token and redirect to dashboard
        await storeToken(response.token);
        navigate('/dashboard');
      } else {
        // Prepare for OTP step. Some backends return hints like requires_otp, message, session ids.
        const requiresOtp = response?.requires_otp || response?.otp_required || !response?.token;
        if (requiresOtp) {
          setStep('otp');
          setServerMsg(response?.message || 'OTP has been sent to your phone.');
          // Capture any session identifiers returned by backend
          const sess = {};
          if (response?.session_id) sess.session_id = response.session_id;
          if (response?.transaction_id) sess.transaction_id = response.transaction_id;
          // Many backends require a verificationId for OTP verification
          if (response?.verificationId) sess.verificationId = response.verificationId;
          if (response?.verification_id) sess.verificationId = response.verification_id;
          setSessionInfo(sess);
        } else {
          setError('Login failed. Invalid response from server.');
        }
      }
    } catch (error) {
      setError(error.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (e) => {
    // Only digits, typical 4-6 length; allow up to 8 to be safe
    const code = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtpCode(code);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length < 4) {
      setError('Please enter the OTP sent to your phone');
      return;
    }
    setVerifying(true);
    setError('');
    try {
      const formattedPhone = phoneNumber.startsWith('+91') ? phoneNumber : `+91${phoneNumber}`;
      const response = await verifyOtp(formattedPhone, otpCode, sessionInfo);
      if (response && response.token) {
        await storeToken(response.token);
        navigate('/dashboard');
      } else {
        setError(response?.error || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setError(err?.error || err?.message || 'OTP verification failed. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h2 className="login-title">Dudhiya Collection</h2>
        <div className="logo-container">
          <div className="logo">DC</div>
        </div>

        {error && <div className="error-message">{error}</div>}
        {serverMsg && !error && <div className="info-message">{serverMsg}</div>}

        {step === 'phone' && (
          <form onSubmit={handleLogin}>
            <div className="input-group">
              <FontAwesomeIcon icon={faUser} className="input-icon" />
              <input
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                placeholder="Enter your 10-digit phone number"
                disabled={loading}
                required
              />
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={loading}
            >
              {loading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                'Login'
              )}
            </button>
          </form>
        )}

        {step === 'otp' && (
          <form onSubmit={handleVerifyOtp}>
            <div className="input-group">
              <input
                type="tel"
                value={otpCode}
                onChange={handleOtpChange}
                placeholder="Enter OTP"
                disabled={verifying}
                required
              />
            </div>
            <button
              type="submit"
              className="login-button"
              disabled={verifying || otpCode.length < 4}
            >
              {verifying ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                'Verify OTP'
              )}
            </button>
            <button
              type="button"
              className="secondary-button"
              disabled={verifying}
              onClick={() => {
                setStep('phone');
                setOtpCode('');
                setServerMsg('');
              }}
            >
              Change Number
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login; 